import { useT } from "@transifex/react";
import type { SchoolClass } from "p6m-p6u";
import { useState } from "react";
import type { ClassActivityCategory } from "../../../constants/ClassActivityConstants";
import type { SchoolClassChangeHandler } from "../ClassActivityCardsOverview.types";
import PhaseSixIcon, { type TIconNames } from "../../../basic/phaseSixIcon/PhaseSixIcon";
import { ClassAssignmentPopup } from "./ClassAssignmentPopup";

export interface ClassAssignmentTagProps {
    availableSchoolClasses: SchoolClass[];
    onSchoolClassChange: SchoolClassChangeHandler;
    classActivityCategory: ClassActivityCategory;

    selectedSchoolClassId?: string;
    isShared?: boolean;
    isSharedTest?: boolean;
}

const renderTag = (title: string, icon: TIconNames, onClick: () => void) => {
    const baseStyles =
        "border border-iris-100 rounded-pill px-s py-xxs flex flex-row items-center gap-xs cursor-pointer";
    const variantStyles = "bg-white hover:bg-iris-100/5";

    return (
        <button
            type="button"
            data-testid="class-assignment-tag"
            onClick={onClick}
            className={`${baseStyles} ${variantStyles}`}
        >
            <span className="text-l text-iris-100 line-clamp-2">{title}</span>
            <PhaseSixIcon
                name={icon}
                className="text-iris-100 text-xl"
            />
        </button>
    );
};

export const ClassAssignmentTag = (props: ClassAssignmentTagProps) => {
    const t = useT();

    const t_addClass = t("Add class", { _tags: "SchoolClasses, ClassTag" });
    const t_newClass = t("New class", { _tags: "SchoolClasses, ClassTag" });

    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const selectedSchoolClass = props.availableSchoolClasses.find((c) => c.token === props.selectedSchoolClassId);

    const renderClassTag = () => {
        if (selectedSchoolClass) {
            return renderTag(selectedSchoolClass.name, "edit-avatar", handleOpenModal);
        }

        if (props.availableSchoolClasses.length === 0) {
            return renderTag(t_newClass, "add", handleOpenModal);
        }

        return renderTag(t_addClass, "add", handleOpenModal);
    };

    return (
        <div>
            {renderClassTag()}
            <ClassAssignmentPopup
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                availableSchoolClasses={props.availableSchoolClasses}
                selectedSchoolClassId={props.selectedSchoolClassId}
                classActivityCategory={props.classActivityCategory}
                onSchoolClassChange={props.onSchoolClassChange}
                isShared={props.isShared}
                isSharedTest={props.isSharedTest}
            />
        </div>
    );
};
